import { useMemo } from 'react';
import { useConfiguratorStore } from '@/state/configuratorStore';

const CATEGORY_LABELS: Record<string, string> = {
  soccer: 'Soccer',
  jacket: 'Jackets',
  hockey: 'Hockey',
  training: 'Training',
  netball: 'Netball',
  rugby: 'Rugby',
};

export function LeftPanel() {
  const products = useConfiguratorStore(s => s.products);
  const product = useConfiguratorStore(s => s.product);
  const template = useConfiguratorStore(s => s.template);
  const setProduct = useConfiguratorStore(s => s.setProduct);
  const setTemplate = useConfiguratorStore(s => s.setTemplate);

  const groups = useMemo(() => {
    const map = new Map<string, typeof products>();
    for (const p of products) {
      const key = p.category || 'other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    // Stable order: known categories first, then alpha
    const order = ['soccer', 'training', 'hockey', 'rugby', 'netball', 'jacket'];
    return [...map.entries()].sort((a, b) => {
      const ia = order.indexOf(a[0]);
      const ib = order.indexOf(b[0]);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a[0].localeCompare(b[0]);
    });
  }, [products]);

  return (
    <aside className="panel panel--left" style={{ width: 'var(--left-w)' }}>
      <section className="panel__section">
        <h2 className="panel__title">Product</h2>
        <label className="product-select">
          <span className="product-select__meta">
            <strong>{product.name}</strong>
            <span className="product-select__cat">{product.category}</span>
          </span>
          <select
            className="product-select__control"
            value={product.id}
            onChange={e => void setProduct(e.target.value)}
            aria-label="Select product"
          >
            {groups.map(([cat, list]) => (
              <optgroup key={cat} label={CATEGORY_LABELS[cat] ?? cat}>
                {list.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <svg
            className="product-select__chevron"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            aria-hidden="true"
          >
            <path
              d="M3 5l4 4 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </label>
      </section>

      <section className="panel__section panel__section--grow">
        <h2 className="panel__title">Base design</h2>
        <p className="empty-note" style={{ marginBottom: 10 }}>
          Sets zone shapes & default colours. Pattern areas → right <strong>Customise</strong> tab.
        </p>
        <div className="template-grid base-design-grid">
          {product.designs.map(d => (
            <button
              key={d.id}
              type="button"
              className={`template-card${template?.id === d.id ? ' is-active' : ''}`}
              onClick={() => void setTemplate(d.id)}
              title={d.name}
            >
              <img src={d.thumb} alt={d.name} loading="lazy" />
              <span>{d.name}</span>
            </button>
          ))}
        </div>
      </section>
    </aside>
  );
}
